export function getTopSuggestions(data, route) {
  // === UTILITIES ===
  const isObj = v => v && typeof v === "object" && !Array.isArray(v);
  const isArr = Array.isArray;

  // Fields to exclude from analysis (IDs, internal references, API identifiers)
  const excludePatterns = /(_id|[^a-z]id$|uuid|guid|token|key|hash|songstats|spotify_id|apple_id|deezer_id|user_id|track_id|artist_id|album_id|playlist_id|external_id|api_key|session|timestamp_ms|created_at|updated_at)/i;

  const shouldExcludeField = (key) => {
    return excludePatterns.test(key);
  };

  const formatMetricName = (key) => {
    // Remove technical prefixes and clean up the name
    let name = key.split('.').pop(); // Get last part of path
    name = name.replace(/(_id|Id|ID)$/g, ''); // Remove ID suffixes
    name = name.replace(/^(songstats_|songstats)/gi, ''); // Remove API prefixes
    name = name.replace(/[_-]/g, ' '); // Replace underscores/hyphens with spaces
    name = name.replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
    return name.trim();
  };

  // Country code to name mapping
  const countryMap = {
    'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
    'DE': 'Germany', 'FR': 'France', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
    'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
    'DK': 'Denmark', 'FI': 'Finland', 'IE': 'Ireland', 'PT': 'Portugal', 'GR': 'Greece',
    'PL': 'Poland', 'CZ': 'Czech Republic', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria',
    'HR': 'Croatia', 'SK': 'Slovakia', 'SI': 'Slovenia', 'LT': 'Lithuania', 'LV': 'Latvia',
    'EE': 'Estonia', 'JP': 'Japan', 'CN': 'China', 'KR': 'South Korea', 'IN': 'India',
    'SG': 'Singapore', 'MY': 'Malaysia', 'TH': 'Thailand', 'ID': 'Indonesia', 'PH': 'Philippines',
    'VN': 'Vietnam', 'NZ': 'New Zealand', 'ZA': 'South Africa', 'BR': 'Brazil', 'MX': 'Mexico',
    'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'VE': 'Venezuela',
    'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia', 'IL': 'Israel', 'TR': 'Turkey',
    'EG': 'Egypt', 'NG': 'Nigeria', 'KE': 'Kenya', 'RU': 'Russia', 'UA': 'Ukraine'
  };

  const formatLocation = (location) => {
    if (!location || typeof location !== 'string') return location;
    const upper = location.toUpperCase();
    // If it's a country code, convert it
    if (countryMap[upper]) return countryMap[upper];
    // Otherwise return as-is (likely already a city name)
    return location;
  };

  const safeNum = v => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") {
      // Filter out unrealistic numbers (likely IDs or errors)
      if (!isFinite(v) || Math.abs(v) > 1e15) return null;
      return v;
    }
    const s = String(v).replace(/[^0-9.\-eE]+/g, "");
    const n = Number(s);
    // Filter out unrealistic numbers
    if (!Number.isFinite(n) || Math.abs(n) > 1e15) return null;
    return n;
  };

  const formatNumber = n => {
    if (!isFinite(n)) return "0";
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    if (abs >= 100) return Math.round(n).toString();
    if (abs >= 1) return n.toFixed(1);
    return n.toFixed(2);
  };

  const looksLikeDate = s => {
    if (!s || typeof s !== "string") return false;
    return /\d{4}-\d{2}-\d{2}|T\d{2}:\d{2}|\/\d{1,2}\/\d{2,4}|\d{1,2}-\w{3}-\d{2,4}/i.test(s);
  };

  // === DEEP DATA EXTRACTION ===
  function extractAllData(obj, prefix = "", result = { numeric: {}, text: {}, arrays: {}, nested: {}, dates: {}, booleans: {} }) {
    if (isArr(obj)) {
      result.arrays[prefix || "root"] = obj;
      obj.forEach((item, i) => {
        if (isObj(item) || isArr(item)) {
          extractAllData(item, `${prefix}[${i}]`, result);
        }
      });
    } else if (isObj(obj)) {
      result.nested[prefix || "root"] = obj;
      Object.keys(obj).forEach(key => {
        const path = prefix ? `${prefix}.${key}` : key;
        const val = obj[key];

        if (isArr(val) || isObj(val)) {
          extractAllData(val, path, result);
        } else {
          if (typeof val === "boolean") {
            result.booleans[path] = val;
          } else {
            const num = safeNum(val);
            if (num !== null) {
              result.numeric[path] = num;
            } else if (typeof val === "string" && val.trim()) {
              if (looksLikeDate(val)) {
                result.dates[path] = val;
              } else {
                result.text[path] = val;
              }
            }
          }
        }
      });
    }
    return result;
  }

  // === TIME SERIES DETECTION & TREND ANALYSIS ===
  function analyzeTimeSeries(arr, path) {
    if (!isArr(arr) || arr.length < 2) return null;

    const samples = arr.filter(isObj);
    if (samples.length < 2) return null;

    const keys = new Set();
    samples.forEach(item => Object.keys(item).forEach(k => keys.add(k)));
    const keyArray = Array.from(keys);

    // Find date field
    const dateKey = keyArray.find(k => samples.some(item => looksLikeDate(item[k])));

    // Find numeric fields
    const numericKeys = keyArray.filter(k =>
      samples.some(item => safeNum(item[k]) !== null)
    );

    if (numericKeys.length === 0) return null;

    const trends = {};
    numericKeys.forEach(metricKey => {
      const points = samples
        .map(item => ({
          value: safeNum(item[metricKey]),
          date: dateKey ? item[dateKey] : null,
          raw: item
        }))
        .filter(p => p.value !== null);

      if (points.length < 2) return;

      if (dateKey) {
        points.sort((a, b) => new Date(a.date) - new Date(b.date));
      }

      const values = points.map(p => p.value);
      const last = values[values.length - 1];
      const prev = values[values.length - 2];
      const first = values[0];
      const avg = values.reduce((s, v) => s + v, 0) / values.length;
      const max = Math.max(...values);
      const min = Math.min(...values);

      const changeFromPrev = prev !== 0 ? ((last - prev) / Math.abs(prev)) * 100 : (last > 0 ? 100 : 0);
      const changeFromFirst = first !== 0 ? ((last - first) / Math.abs(first)) * 100 : (last > 0 ? 100 : 0);

      // Detect trend direction with better thresholds
      let trendDirection = "stable";
      if (changeFromPrev > 5) trendDirection = "rising";
      else if (changeFromPrev < -5) trendDirection = "falling";

      // Volatility
      const variance = values.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / values.length;
      const stdDev = Math.sqrt(variance);
      const volatility = avg !== 0 ? (stdDev / Math.abs(avg)) * 100 : 0;

      // Momentum (comparing recent vs early periods)
      const midpoint = Math.floor(values.length / 2);
      const earlyAvg = values.slice(0, midpoint).reduce((s, v) => s + v, 0) / midpoint;
      const recentAvg = values.slice(midpoint).reduce((s, v) => s + v, 0) / (values.length - midpoint);
      const momentum = earlyAvg !== 0 ? ((recentAvg - earlyAvg) / Math.abs(earlyAvg)) * 100 : 0;

      trends[metricKey] = {
        metricKey,
        last,
        prev,
        first,
        avg,
        max,
        min,
        changeFromPrev,
        changeFromFirst,
        trendDirection,
        volatility,
        momentum,
        dataPoints: points.length,
        hasDateField: !!dateKey
      };
    });

    return { path, dateKey, trends, sampleData: samples.slice(0, 3) };
  }

  // === INTELLIGENT KEYWORD MATCHING ===
  const categoryPatterns = {
    revenue: /revenue|income|sales|earn|profit|margin|money|dollar|price|cost|payment|mrr|arr|billing|subscription/i,
    streams: /stream|play|view|listen|watch|impression|reach|audio|video/i,
    followers: /follower|fan|subscriber|audience|member|user|community|customer/i,
    engagement: /engage|like|comment|share|react|interact|click|ctr|conversion|bounce|session/i,
    downloads: /download|install|acquisition/i,
    retention: /retention|churn|active|dau|mau|wau|returning|repeat|loyal/i,
    growth: /growth|increase|gain|new|added|acquisition|trend/i,
    performance: /performance|success|rating|score|rank|quality|efficiency/i,
    geographic: /country|city|region|location|geo|territory|market|top.*country|top.*city/i,
    time: /time|duration|length|period|daily|weekly|monthly|date|day|month|year|hour|minute/i,
    social: /instagram|tiktok|twitter|facebook|youtube|social|post|story|reel|linkedin/i,
    content: /content|post|article|blog|video|track|song|album|release|publication/i,
    campaign: /campaign|ad|advertisement|promo|marketing|spend|budget/i,
    playlist: /playlist|chart|ranking|position|featured/i,
    email: /email|newsletter|mail|open.rate|click.rate/i,
    demographic: /age|gender|demographic|male|female|teen|adult|audience/i,
    product: /product|item|sku|inventory|stock|catalog/i,
    feedback: /rating|review|feedback|satisfaction|nps|score/i,
    traffic: /traffic|visit|visitor|pageview|session/i,
    conversion: /conversion|signup|registration|lead|purchase/i
  };

  function categorizeKey(key) {
    const lower = key.toLowerCase();
    const matches = [];
    Object.entries(categoryPatterns).forEach(([cat, pattern]) => {
      if (pattern.test(lower)) matches.push(cat);
    });
    return matches.length > 0 ? matches : ["general"];
  }

  // === ENHANCED SUGGESTION GENERATOR ===
  function generateSuggestion({ title, insight, actions, tags, confidence, priority, dataContext }) {
    const followUpMap = {
      revenue: "Want help optimizing your revenue streams or pricing strategy?",
      streams: "Ready to amplify your reach with a targeted content strategy?",
      followers: "Should I design a growth campaign to accelerate follower acquisition?",
      engagement: "Want to create a content plan that drives deeper engagement?",
      growth: "Ready to scale this momentum with strategic initiatives?",
      geographic: "Should we develop location-specific growth tactics?",
      campaign: "Want to optimize your campaign performance and ROI?",
      playlist: "Need help with playlist positioning strategies?",
      retention: "Should we build a retention improvement roadmap?",
      social: "Want to refine your social media approach for better results?",
      content: "Ready to develop a high-performing content strategy?",
      product: "Want help optimizing your product mix or inventory?",
      traffic: "Should we work on converting more of your traffic?",
      conversion: "Ready to improve your conversion funnel?",
      default: "Would you like a detailed action plan for this opportunity?"
    };

    const primaryTag = tags[0] || "default";

    return {
      title,
      insight,
      actions,
      confidence,
      priority,
      tags,
      followUpQuestion: followUpMap[primaryTag] || followUpMap.default,
      route: route || "/insights",
      dataContext
    };
  }

  // === MAIN ANALYSIS ENGINE ===
  const extracted = extractAllData(data);
  const suggestions = [];

  // === ANALYZE CONTENT/TRACK PERFORMANCE ===
  const contentData = {
    trackName: null,
    songName: null,
    contentName: null,
    streams: null,
    views: null,
    plays: null,
    listeners: null,
    engagement: null
  };

  // Check root level for content performance data
  if (data && typeof data === 'object') {
    contentData.trackName = data.trackName || data.songName || data.contentName || data.name;
    contentData.streams = safeNum(data.streams || data.plays || data.views);
    contentData.listeners = safeNum(data.listeners || data.uniqueListeners);
    contentData.engagement = safeNum(data.engagement || data.engagementRate);
  }

  // Generate content performance insights
  if (contentData.trackName && contentData.streams !== null) {
    const track = contentData.trackName;
    const streams = contentData.streams;

    if (streams >= 100000) {
      suggestions.push(generateSuggestion({
        title: `🚀 ${formatNumber(streams)} streams on "${track}"`,
        insight: `🚀 ${formatNumber(streams)} streams on "${track}". Major milestone hit. Time to pitch playlists.`,
        actions: [
          `Pitch "${track}" to major playlist curators now`,
          "Create behind-the-scenes content",
          "Use this in press releases and pitches",
          "Analyze your top listener demographics"
        ],
        tags: ["streams", "content", "performance"],
        confidence: 0.91,
        priority: 1,
        dataContext: { trackName: track, streams }
      }));
    } else if (streams >= 10000) {
      suggestions.push(generateSuggestion({
        title: `📈 ${formatNumber(streams)} streams on "${track}"`,
        insight: `📈 ${formatNumber(streams)} streams on "${track}". Strong momentum. Push it harder.`,
        actions: [
          `Run targeted ads for "${track}"`,
          "Submit to indie playlists",
          "Create shareable content (lyric videos)",
          "Engage fans who are streaming it"
        ],
        tags: ["streams", "content", "growth"],
        confidence: 0.87,
        priority: 1,
        dataContext: { trackName: track, streams }
      }));
    } else if (streams >= 1000) {
      suggestions.push(generateSuggestion({
        title: `🌱 ${formatNumber(streams)} streams on "${track}"`,
        insight: `🌱 ${formatNumber(streams)} streams on "${track}". Early traction. Keep promoting.`,
        actions: [
          "Share it daily across all socials",
          "Reach out to micro-influencers",
          "Add to Spotify playlists",
          "Ask fans to playlist it"
        ],
        tags: ["streams", "content", "growth"],
        confidence: 0.83,
        priority: 1,
        dataContext: { trackName: track, streams }
      }));
    } else if (streams > 0) {
      suggestions.push(generateSuggestion({
        title: `🎤 ${formatNumber(streams)} streams on "${track}"`,
        insight: `🎤 ${formatNumber(streams)} streams on "${track}". Just launched. Time to push.`,
        actions: [
          "Share with your personal network",
          "Post daily about the track",
          "Pitch to emerging artist playlists",
          "Cross-promote with other artists"
        ],
        tags: ["streams", "content", "growth"],
        confidence: 0.80,
        priority: 1,
        dataContext: { trackName: track, streams }
      }));
    }

    // Add listener-to-stream ratio insight if we have both
    if (contentData.listeners !== null && contentData.listeners > 0) {
      const ratio = streams / contentData.listeners;
      if (ratio > 5) {
        suggestions.push(generateSuggestion({
          title: `🔁 ${ratio.toFixed(1)}x replay rate on "${track}"`,
          insight: `🔁 ${ratio.toFixed(1)}x replay rate on "${track}". Fans love it. Reach more people.`,
          actions: [
            "Focus on reaching new audiences",
            `Make "${track}" your flagship song`,
            "Create remixes or acoustic versions",
            "Target lookalike audiences"
          ],
          tags: ["engagement", "content", "performance"],
          confidence: 0.85,
          priority: 2,
          dataContext: { trackName: track, streams, listeners: contentData.listeners, ratio }
        }));
      }
    }
  }

  // === ANALYZE FINANCIAL DATA (REVENUE, EXPENSES, ROI) ===
  const financialData = {
    revenues: [],
    expenses: [],
    totalRevenues: null,
    totalExpenses: null,
    roi: null,
    highestCategory: null,
    netProfit: null
  };

  // Extract financial arrays
  if (extracted.arrays['revenues'] || extracted.arrays['root']) {
    const revArray = extracted.arrays['revenues'] || (Array.isArray(data.revenues) ? data.revenues : []);
    if (revArray.length > 0) financialData.revenues = revArray;
  }

  if (extracted.arrays['expenses'] || extracted.arrays['root']) {
    const expArray = extracted.arrays['expenses'] || (Array.isArray(data.expenses) ? data.expenses : []);
    if (expArray.length > 0) financialData.expenses = expArray;
  }

  // Extract financial totals
  Object.entries(extracted.numeric).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('totalrevenue') || lowerKey === 'totalrevenues') {
      financialData.totalRevenues = value;
    } else if (lowerKey.includes('totalexpense') || lowerKey === 'totalexpenses') {
      financialData.totalExpenses = value;
    } else if (lowerKey === 'roi' || lowerKey.includes('returnon')) {
      financialData.roi = value;
    }
  });

  Object.entries(extracted.text).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('highestcategory') || lowerKey.includes('top_category')) {
      financialData.highestCategory = value;
    }
  });

  // Calculate net profit if we have totals
  if (financialData.totalRevenues !== null && financialData.totalExpenses !== null) {
    financialData.netProfit = financialData.totalRevenues - financialData.totalExpenses;
  }

  // === GENERATE FINANCIAL INSIGHTS ===
  const hasFinancialData = financialData.totalRevenues !== null ||
    financialData.revenues.length > 0 ||
    financialData.expenses.length > 0;

  if (hasFinancialData) {
    // ROI Analysis
    if (financialData.roi !== null && financialData.roi > 0) {
      if (financialData.roi > 150) {
        suggestions.push(generateSuggestion({
          title: `💰 Exceptional ${financialData.roi.toFixed(0)}% ROI`,
          insight: `💰 Your ${financialData.roi.toFixed(1)}% return on investment is outstanding. Every dollar spent generates ${(financialData.roi / 100 + 1).toFixed(2)} back—this efficiency is rare and should be maximized.`,
          actions: [
            "Scale your current investments aggressively to compound these returns",
            "Document your successful strategies to maintain this ROI as you grow",
            "Reallocate budget from lower-performing areas to these high-ROI activities",
            "Set ROI benchmarks for all future campaigns based on this success"
          ],
          tags: ["revenue", "performance", "growth"],
          confidence: 0.93,
          priority: 1,
          dataContext: { roi: financialData.roi, totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses }
        }));
      } else if (financialData.roi > 100) {
        suggestions.push(generateSuggestion({
          title: `📈 Strong ${financialData.roi.toFixed(0)}% ROI Performance`,
          insight: `📈 Your ${financialData.roi.toFixed(1)}% ROI means you're more than doubling your investment. This solid performance creates sustainable growth momentum.`,
          actions: [
            "Increase investment in your highest-ROI channels by 30-50%",
            "Analyze which specific activities drive the best returns",
            "Test optimizations to push ROI even higher without sacrificing growth",
            "Share successful strategies across all revenue streams"
          ],
          tags: ["revenue", "performance", "growth"],
          confidence: 0.88,
          priority: 1,
          dataContext: { roi: financialData.roi, totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses }
        }));
      } else if (financialData.roi > 50) {
        suggestions.push(generateSuggestion({
          title: `✅ Healthy ${financialData.roi.toFixed(0)}% ROI`,
          insight: `✅ Your ${financialData.roi.toFixed(1)}% return on investment is positive and sustainable. There's room to optimize further for better returns.`,
          actions: [
            "Identify your top 3 revenue sources and double down on those",
            "Review expenses to find areas where you can reduce costs by 10-20%",
            "Test new revenue streams with small investments to find winners",
            "Set a target to improve ROI to 80-100% over the next quarter"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.82,
          priority: 2,
          dataContext: { roi: financialData.roi }
        }));
      } else if (financialData.roi > 0) {
        suggestions.push(generateSuggestion({
          title: `⚠️ ${financialData.roi.toFixed(0)}% ROI Needs Optimization`,
          insight: `⚠️ Your ${financialData.roi.toFixed(1)}% ROI is positive but low. Small improvements here could dramatically impact profitability.`,
          actions: [
            "Cut or reduce spending on activities with negative or near-zero returns",
            "Focus on your most profitable revenue sources and scale those first",
            "Negotiate better rates with service providers to reduce costs",
            "Run experiments to find higher-ROI alternatives to current strategies"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.85,
          priority: 1,
          dataContext: { roi: financialData.roi }
        }));
      }
    } else if (financialData.roi !== null && financialData.roi < 0) {
      suggestions.push(generateSuggestion({
        title: `🚨 Negative ROI Requires Immediate Action`,
        insight: `🚨 Your ROI is ${financialData.roi.toFixed(1)}%, meaning you're spending more than you're earning. This is unsustainable and needs urgent attention.`,
        actions: [
          "Immediately pause or reduce spending on non-essential activities",
          "Focus exclusively on revenue-generating activities until ROI turns positive",
          "Review all expenses and cut anything that doesn't directly drive revenue",
          "Consider raising prices or finding lower-cost alternatives for services"
        ],
        tags: ["revenue", "performance"],
        confidence: 0.92,
        priority: 1,
        dataContext: { roi: financialData.roi, totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses }
      }));
    }

    // Revenue vs Expenses Analysis
    if (financialData.totalRevenues !== null && financialData.totalExpenses !== null) {
      const netProfit = financialData.netProfit;
      const profitMargin = (netProfit / financialData.totalRevenues) * 100;

      if (netProfit > 0) {
        if (profitMargin > 50) {
          suggestions.push(generateSuggestion({
            title: `💎 Excellent ${profitMargin.toFixed(0)}% Profit Margin`,
            insight: `💎 You're generating $${formatNumber(financialData.totalRevenues)} in revenue against $${formatNumber(financialData.totalExpenses)} in expenses, yielding $${formatNumber(netProfit)} net profit. This ${profitMargin.toFixed(1)}% margin is exceptional.`,
            actions: [
              "Reinvest a portion of profits to accelerate growth",
              "Consider strategic investments in high-potential opportunities",
              "Build cash reserves while maintaining this strong margin",
              "Document your cost efficiency strategies for scaling"
            ],
            tags: ["revenue", "performance"],
            confidence: 0.89,
            priority: 1,
            dataContext: { totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses, netProfit, profitMargin }
          }));
        } else if (profitMargin > 20) {
          suggestions.push(generateSuggestion({
            title: `💚 Profitable Operations: ${formatNumber(netProfit)} Net Profit`,
            insight: `💚 You're earning ${formatNumber(financialData.totalRevenues)} with ${formatNumber(financialData.totalExpenses)} in expenses, resulting in ${formatNumber(netProfit)} profit (${profitMargin.toFixed(1)}% margin). This is healthy and sustainable.`,
            actions: [
              "Identify opportunities to increase revenue without proportionally increasing costs",
              "Look for expense optimizations to improve your margin to 30%+",
              "Scale winning revenue streams while maintaining cost efficiency",
              "Set aside 20-30% of profits for growth investments"
            ],
            tags: ["revenue", "performance"],
            confidence: 0.85,
            priority: 2,
            dataContext: { totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses, netProfit, profitMargin }
          }));
        } else {
          suggestions.push(generateSuggestion({
            title: `⚖️ Thin ${profitMargin.toFixed(1)}% Profit Margin`,
            insight: `⚖️ While you're profitable with ${formatNumber(netProfit)} net profit from ${formatNumber(financialData.totalRevenues)} revenue, your ${profitMargin.toFixed(1)}% margin leaves little room for error.`,
            actions: [
              "Focus on increasing revenue through upsells or new offerings",
              "Audit expenses to find 15-20% in potential savings",
              "Negotiate better rates with vendors and service providers",
              "Prioritize high-margin activities over low-margin ones"
            ],
            tags: ["revenue", "performance"],
            confidence: 0.83,
            priority: 1,
            dataContext: { totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses, netProfit, profitMargin }
          }));
        }
      } else if (netProfit < 0) {
        suggestions.push(generateSuggestion({
          title: `🚨 Operating at Loss: ${formatNumber(Math.abs(netProfit))} Deficit`,
          insight: `🚨 You're spending $${formatNumber(financialData.totalExpenses)} while earning $${formatNumber(financialData.totalRevenues)}, creating a $${formatNumber(Math.abs(netProfit))} loss. This requires immediate correction.`,
          actions: [
            "Immediately cut non-essential expenses by at least 30%",
            "Focus all efforts on your highest-revenue activities",
            "Pause new spending until you reach break-even",
            "Create a 90-day plan to achieve positive cash flow"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.95,
          priority: 1,
          dataContext: { totalRevenues: financialData.totalRevenues, totalExpenses: financialData.totalExpenses, netProfit, loss: Math.abs(netProfit) }
        }));
      }
    }

    // Category Performance
    if (financialData.highestCategory) {
      const category = financialData.highestCategory;

      // Analyze revenue by category if available
      if (financialData.revenues.length > 0) {
        const categoryRevenues = financialData.revenues
          .filter(r => r.category === category)
          .reduce((sum, r) => sum + (safeNum(r.amount) || 0), 0);

        const categoryShare = financialData.totalRevenues
          ? (categoryRevenues / financialData.totalRevenues) * 100
          : 0;

        if (categoryRevenues > 0) {
          suggestions.push(generateSuggestion({
            title: `🎯 ${category} Leads Revenue Generation`,
            insight: `🎯 ${category} is your highest-performing category, generating $${formatNumber(categoryRevenues)}${categoryShare > 0 ? ` (${categoryShare.toFixed(0)}% of total revenue)` : ''}. This is your strongest revenue driver.`,
            actions: [
              `Analyze what makes ${category} successful and replicate those strategies`,
              `Increase investment in ${category} by 30-50% to maximize returns`,
              `Create new offerings or campaigns within the ${category} category`,
              `Use ${category} insights to improve performance in other categories`
            ],
            tags: ["revenue", "performance", "growth"],
            confidence: 0.86,
            priority: 2,
            dataContext: { category, categoryRevenues, categoryShare }
          }));
        }
      }
    }

    // Revenue Source Diversification
    if (financialData.revenues.length >= 2) {
      const sources = {};
      financialData.revenues.forEach(r => {
        const source = r.source || 'Other';
        sources[source] = (sources[source] || 0) + (safeNum(r.amount) || 0);
      });

      const sourceEntries = Object.entries(sources).sort((a, b) => b[1] - a[1]);
      const topSource = sourceEntries[0];
      const topSourceShare = financialData.totalRevenues
        ? (topSource[1] / financialData.totalRevenues) * 100
        : 0;

      if (topSourceShare > 70) {
        suggestions.push(generateSuggestion({
          title: `⚠️ Revenue Concentration Risk: ${topSource[0]}`,
          insight: `⚠️ ${topSource[0]} generates ${formatNumber(topSource[1])} (${topSourceShare.toFixed(0)}% of revenue). This concentration creates vulnerability if this source declines.`,
          actions: [
            "Develop 2-3 additional revenue sources to reduce dependency",
            `While maintaining ${topSource[0]} performance, test alternative platforms`,
            "Set a goal to diversify so no single source exceeds 50% of revenue",
            "Build contingency plans in case your primary source changes"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.84,
          priority: 2,
          dataContext: { topSource: topSource[0], topSourceRevenue: topSource[1], concentration: topSourceShare }
        }));
      } else if (sourceEntries.length >= 3) {
        suggestions.push(generateSuggestion({
          title: `✅ Well-Diversified Revenue: ${sourceEntries.length} Sources`,
          insight: `✅ You're generating revenue from ${sourceEntries.length} different sources, with ${topSource[0]} leading at ${formatNumber(topSource[1])} (${topSourceShare.toFixed(0)}%). This diversification reduces risk.`,
          actions: [
            "Continue maintaining all revenue streams while optimizing each",
            `Scale ${topSource[0]} as your primary source while testing others`,
            "Identify which sources have the best growth potential",
            "Set category-specific goals for each revenue stream"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.81,
          priority: 3,
          dataContext: { sourceCount: sourceEntries.length, topSource: topSource[0], diversification: true }
        }));
      }
    }

    // Expense Management
    if (financialData.expenses.length >= 2) {
      const expensesByCategory = {};
      financialData.expenses.forEach(e => {
        const category = e.category || 'Other';
        expensesByCategory[category] = (expensesByCategory[category] || 0) + (safeNum(e.amount) || 0);
      });

      const expenseEntries = Object.entries(expensesByCategory).sort((a, b) => b[1] - a[1]);
      const topExpense = expenseEntries[0];
      const topExpenseShare = financialData.totalExpenses
        ? (topExpense[1] / financialData.totalExpenses) * 100
        : 0;

      if (topExpenseShare > 50) {
        suggestions.push(generateSuggestion({
          title: `💸 ${topExpense[0]} Dominates Expenses`,
          insight: `💸 ${topExpense[0]} accounts for ${formatNumber(topExpense[1])} (${topExpenseShare.toFixed(0)}% of expenses). This is your largest cost center and primary optimization opportunity.`,
          actions: [
            `Review all ${topExpense[0]} expenses to find 15-20% in savings`,
            "Negotiate better rates or find more cost-effective alternatives",
            `Ensure every ${topExpense[0]} dollar spent delivers measurable ROI`,
            "Set strict budgets and approval processes for this category"
          ],
          tags: ["revenue", "performance"],
          confidence: 0.83,
          priority: 2,
          dataContext: { category: topExpense[0], amount: topExpense[1], share: topExpenseShare }
        }));
      }
    }
  }

  // === ANALYZE GEOGRAPHIC & DEMOGRAPHIC PATTERNS ===
  const geoData = {
    topCity: null,
    topCountry: null,
    growthMarket: null,
    growthPercentage: null,
    trendMessage: null
  };

  // Check root level data first (not nested)
  if (data && typeof data === 'object') {
    if (data.topCity) geoData.topCity = data.topCity;
    if (data.topCountry) geoData.topCountry = data.topCountry;
    if (data.growthMarket) geoData.growthMarket = data.growthMarket;
    if (data.growthPercentage) geoData.growthPercentage = safeNum(data.growthPercentage);
    if (data.trendMessage) geoData.trendMessage = data.trendMessage;
  }

  // Also check extracted data as fallback
  Object.entries(extracted.text).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    if (!geoData.topCity && (lowerKey.includes('topcity') || lowerKey.includes('top_city'))) {
      geoData.topCity = value;
    } else if (!geoData.topCountry && (lowerKey.includes('topcountry') || lowerKey.includes('top_country'))) {
      geoData.topCountry = value;
    } else if (!geoData.growthMarket && (lowerKey.includes('growthmarket') || lowerKey.includes('growth_market'))) {
      geoData.growthMarket = value;
    } else if (!geoData.trendMessage && lowerKey.includes('trend') && lowerKey.includes('message')) {
      geoData.trendMessage = value;
    }
  });

  Object.entries(extracted.numeric).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    if (!geoData.growthPercentage && (lowerKey.includes('growthpercentage') || lowerKey.includes('growth_percentage') || lowerKey.includes('growth_percent'))) {
      geoData.growthPercentage = value;
    }
  });

  // Generate geographic insights if we have location data
  if (geoData.topCity || geoData.topCountry) {
    const city = geoData.topCity;
    const country = formatLocation(geoData.topCountry);
    const growthMarket = formatLocation(geoData.growthMarket);
    const growthPct = geoData.growthPercentage;

    // Top performing location
    if (city && country) {
      suggestions.push(generateSuggestion({
        title: `🌟 ${city}, ${country} is #1`,
        insight: `🌟 ${city}, ${country} is your top market. Dominate here.`,
        actions: [
          `Deep-dive ${city} demographics`,
          `Launch ${city}-specific campaigns`,
          `Find similar cities in ${country}`,
          `Partner with local ${city} influencers`
        ],
        tags: ["geographic", "performance"],
        confidence: 0.88,
        priority: 1,
        dataContext: { topCity: city, topCountry: country }
      }));
    } else if (country) {
      suggestions.push(generateSuggestion({
        title: `🌍 ${country} dominates`,
        insight: `🌍 ${country} is your strongest market. Own it.`,
        actions: [
          `Optimize for ${country} preferences`,
          `Research high-potential ${country} cities`,
          `Study ${country} competitors`,
          `Build ${country} partnerships`
        ],
        tags: ["geographic", "performance"],
        confidence: 0.86,
        priority: 1,
        dataContext: { topCountry: country }
      }));
    }

    // Growth market opportunity
    if (growthMarket && growthPct && growthPct > 20) {
      const isGrowthSameAsTop = growthMarket === country || growthMarket === geoData.topCountry;

      if (isGrowthSameAsTop) {
        suggestions.push(generateSuggestion({
          title: `🚀 ${growthMarket} up ${growthPct.toFixed(0)}%`,
          insight: `🚀 ${growthMarket} grew ${growthPct.toFixed(0)}%. Your best market is exploding.`,
          actions: [
            `Increase ${growthMarket} spend by 50-100%`,
            `Launch "Thank You ${growthMarket}" campaign`,
            "Identify what's driving the surge",
            `Set aggressive ${growthMarket} targets`
          ],
          tags: ["geographic", "growth"],
          confidence: 0.92,
          priority: 1,
          dataContext: { growthMarket, growthPercentage: growthPct }
        }));
      } else {
        suggestions.push(generateSuggestion({
          title: `🌱 ${growthMarket} up ${growthPct.toFixed(0)}%`,
          insight: `🌱 ${growthMarket} is your fastest-growing region. Big opportunity.`,
          actions: [
            `Allocate resources to ${growthMarket}`,
            `Create ${growthMarket}-specific content`,
            `Study ${growthMarket} demographics`,
            `Test ${country} tactics in ${growthMarket}`
          ],
          tags: ["geographic", "growth"],
          confidence: 0.89,
          priority: 1,
          dataContext: { growthMarket, growthPercentage: growthPct, topMarket: country }
        }));
      }
    } else if (growthMarket && growthPct && growthPct > 10) {
      suggestions.push(generateSuggestion({
        title: `📈 ${growthMarket} up ${growthPct.toFixed(0)}%`,
        insight: `📈 ${growthMarket} growing steady at ${growthPct.toFixed(0)}%. Maintain momentum.`,
        actions: [
          `Keep current ${growthMarket} strategies`,
          `Document what works in ${growthMarket}`,
          "Consider increasing investment",
          `Monitor ${growthMarket} competitors`
        ],
        tags: ["geographic", "growth"],
        confidence: 0.82,
        priority: 2,
        dataContext: { growthMarket, growthPercentage: growthPct }
      }));
    }

    // If we have both top market and growth market, suggest strategic balance
    if (country && growthMarket && growthMarket !== country && growthMarket !== geoData.topCountry) {
      suggestions.push(generateSuggestion({
        title: `⚖️ ${country} vs ${growthMarket}`,
        insight: `⚖️ Balance: 70% ${country}, 30% ${growthMarket}.`,
        actions: [
          "Maintain dominance in core market",
          "Grow emerging market strategically",
          "Set distinct KPIs per market",
          "Plan for market-specific teams"
        ],
        tags: ["geographic", "growth", "performance"],
        confidence: 0.85,
        priority: 2,
        dataContext: { topMarket: country, growthMarket }
      }));
    }
  }

  // === ANALYZE TIME SERIES DATA ===
  Object.entries(extracted.arrays).forEach(([path, arr]) => {
    const analysis = analyzeTimeSeries(arr, path);
    if (!analysis) return;

    Object.values(analysis.trends).forEach(trend => {
      // Skip if metric should be excluded
      if (shouldExcludeField(trend.metricKey)) {
        return;
      }

      const categories = categorizeKey(trend.metricKey);
      const metricName = (() => {
        const baseName = formatMetricName(trend.metricKey);
        if (trend.metricKey === 'amount') {
          if (path.toLowerCase().includes('revenue')) return 'Revenue Amount';
          if (path.toLowerCase().includes('expense')) return 'Expense Amount';
        }
        return baseName;
      })();

      // Explosive growth
      if (trend.trendDirection === "rising" && trend.changeFromPrev >= 50) {
        suggestions.push(generateSuggestion({
          title: `🚀 Explosive Growth in ${metricName}`,
          insight: `🚀 Your ${metricName} skyrocketed ${trend.changeFromPrev.toFixed(0)}% from ${formatNumber(trend.prev)} to ${formatNumber(trend.last)}. You've found something that works—now it's time to maximize it.`,
          actions: [
            `Identify exactly what triggered the ${trend.changeFromPrev.toFixed(0)}% surge and document it`,
            "Increase investment in this winning channel by 2-3x immediately",
            "Set up real-time monitoring to maintain this growth trajectory",
            "Create case studies showcasing this success for marketing purposes"
          ],
          tags: [...categories, "growth"],
          confidence: 0.92,
          priority: 1,
          dataContext: { metric: trend.metricKey, change: trend.changeFromPrev, trend: "explosive" }
        }));
      }

      // Strong upward trend
      else if (trend.trendDirection === "rising" && trend.changeFromPrev >= 20) {
        suggestions.push(generateSuggestion({
          title: `📈 Strong Momentum in ${metricName}`,
          insight: metricName === "Expense Amount" ? `Your ${metricName} jumped ${trend.changeFromPrev.toFixed(1)}% (${formatNumber(trend.prev)} → ${formatNumber(trend.last)}). Keep an eye on this trend to make sure your budget stays on track.` : `📈 Your ${metricName} jumped ${trend.changeFromPrev.toFixed(1)}% (${formatNumber(trend.prev)} → ${formatNumber(trend.last)}). This upward momentum indicates you're on the right track.`,
          actions: [
            `Analyze the specific tactics that drove the ${trend.changeFromPrev.toFixed(0)}% increase`,
            "Replicate these successful strategies across other channels",
            "Allocate additional budget to amplify what's working",
            "Set more ambitious targets to capitalize on this momentum"
          ],
          tags: [...categories, "growth"],
          confidence: 0.87,
          priority: 1,
          dataContext: { metric: trend.metricKey, change: trend.changeFromPrev, trend: "rising" }
        }));
      }

      // Moderate growth
      else if (trend.trendDirection === "rising" && trend.changeFromPrev >= 10) {
        suggestions.push(generateSuggestion({
          title: `✨ Steady Progress in ${metricName}`,
          insight: `Your ${metricName} improved ${trend.changeFromPrev.toFixed(1)}% to reach ${formatNumber(trend.last)}. This consistent growth is sustainable and worth nurturing.`,
          actions: [
            "Maintain current strategies while testing incremental improvements",
            "Document what's working to ensure consistency",
            "Look for opportunities to accelerate growth without disrupting stability",
            "Share this progress with your team to maintain motivation"
          ],
          tags: [...categories, "growth"],
          confidence: 0.80,
          priority: 2,
          dataContext: { metric: trend.metricKey, change: trend.changeFromPrev, trend: "moderate" }
        }));
      }

      // Sharp decline
      else if (trend.trendDirection === "falling" && trend.changeFromPrev <= -25) {
        suggestions.push(generateSuggestion({
          title: `⚠️ Critical Decline in ${metricName}`,
          insight: metricName === "Expense Amount" ? `Your Expense Amount decreased by ${Math.abs(trend.changeFromPrev).toFixed(0)}% (from ${formatNumber(trend.prev)} to ${formatNumber(trend.last)}). Great job on cost reduction! Review this change to see if it's a sustainable optimization.` : `⚠️ Critical Alert: Your Revenue Amount dropped ${Math.abs(trend.changeFromPrev).toFixed(0)}% from ${formatNumber(trend.prev)} to ${formatNumber(trend.last)}. This requires immediate attention to activate your catalog or start an ad campaign to kick off the algorithm.`,
          actions: [
            `Urgently investigate what caused the ${Math.abs(trend.changeFromPrev).toFixed(0)}% drop`,
            "Compare recent changes in strategy, content, or external factors",
            "Launch a recovery test within 48 hours to reverse the trend",
            "Set up daily monitoring until performance stabilizes"
          ],
          tags: [...categories, "performance"],
          confidence: 0.90,
          priority: 1,
          dataContext: { metric: trend.metricKey, change: trend.changeFromPrev, trend: "critical" }
        }));
      }

      // Declining trend
      else if (trend.trendDirection === "falling" && trend.changeFromPrev <= -10) {
        suggestions.push(generateSuggestion({
          title: `📉 ${metricName} Showing Decline`,
          insight: `📉 Your ${metricName} decreased ${Math.abs(trend.changeFromPrev).toFixed(1)}% (${formatNumber(trend.prev)} → ${formatNumber(trend.last)}). Early intervention can reverse this downward trend.`,
          actions: [
            `Diagnose the root cause of the ${Math.abs(trend.changeFromPrev).toFixed(0)}% decrease`,
            "Review what worked in previous high-performing periods",
            "Test new approaches to break the declining pattern",
            "Check if seasonality or market changes are contributing factors"
          ],
          tags: [...categories, "performance"],
          confidence: 0.84,
          priority: 2,
          dataContext: { metric: trend.metricKey, change: trend.changeFromPrev, trend: "falling" }
        }));
      }

      // High volatility
      if (trend.volatility > 35 && suggestions.filter(s => s.title.includes(metricName)).length === 0) {
        suggestions.push(generateSuggestion({
          title: `🎢 Inconsistent ${metricName} Performance`,
          insight: `🎢 Your ${metricName} fluctuates significantly (${formatNumber(trend.min)} to ${formatNumber(trend.max)}, ${trend.volatility.toFixed(0)}% variance). This unpredictability suggests untapped optimization potential.`,
          actions: [
            "Identify your best-performing periods and replicate their conditions",
            "Eliminate or reduce activities that cause performance drops",
            "Establish consistent processes to stabilize results",
            "Run controlled experiments to understand what drives success"
          ],
          tags: [...categories, "performance"],
          confidence: 0.77,
          priority: 3,
          dataContext: { metric: trend.metricKey, volatility: trend.volatility }
        }));
      }

      // Long-term sustained growth
      else if (trend.changeFromFirst > 100 && trend.dataPoints >= 5 && suggestions.filter(s => s.title.includes(metricName)).length === 0) {
        suggestions.push(generateSuggestion({
          title: `🏆 Exceptional Growth Journey in ${metricName}`,
          insight: `🏆 Your ${metricName} has more than doubled from ${formatNumber(trend.first)} to ${formatNumber(trend.last)} over ${trend.dataPoints} periods. This compound growth demonstrates strong product-market fit.`,
          actions: [
            "Scale investment aggressively in this proven success area",
            "Create a detailed playbook documenting your growth strategies",
            "Explore adjacent markets or verticals using this winning formula",
            "Consider raising targets by 3-5x given your proven growth capability"
          ],
          tags: [...categories, "growth"],
          confidence: 0.89,
          priority: 1,
          dataContext: { metric: trend.metricKey, totalGrowth: trend.changeFromFirst }
        }));
      }

      // Sustained growth
      else if (trend.changeFromFirst > 40 && trend.trendDirection !== "falling" && suggestions.filter(s => s.title.includes(metricName)).length === 0) {
        suggestions.push(generateSuggestion({
          title: `💪 Sustained Growth in ${metricName}`,
          insight: `💪 Your ${metricName} has grown ${trend.changeFromFirst.toFixed(0)}% from ${formatNumber(trend.first)} to ${formatNumber(trend.last)} over ${trend.dataPoints} periods. This consistency is your competitive advantage.`,
          actions: [
            "Increase resources in this area to accelerate growth further",
            "Document your successful approaches for team replication",
            "Set ambitious new milestones to challenge your growth rate",
            "Look for related opportunities to leverage this momentum"
          ],
          tags: [...categories, "growth"],
          confidence: 0.83,
          priority: 2,
          dataContext: { metric: trend.metricKey, totalGrowth: trend.changeFromFirst }
        }));
      }

      // At peak performance
      if (trend.last >= trend.max * 0.95 && suggestions.filter(s => s.title.includes(metricName)).length === 0) {
        suggestions.push(generateSuggestion({
          title: `🎯 ${metricName} at Peak Performance`,
          insight: `🎯 Your ${metricName} is at ${formatNumber(trend.last)}, matching your all-time high of ${formatNumber(trend.max)}. You're operating from a position of strength.`,
          actions: [
            "Maintain current winning strategies without major disruptions",
            "Document this success period as a reference for future campaigns",
            "Test bold experimental approaches while performance is strong",
            "Prepare scalability plans to sustain this level as you grow"
          ],
          tags: [...categories, "performance"],
          confidence: 0.81,
          priority: 2,
          dataContext: { metric: trend.metricKey, current: trend.last, peak: trend.max }
        }));
      }

      // Strong positive momentum
      if (trend.momentum > 30 && suggestions.filter(s => s.title.includes(metricName)).length === 0) {
        suggestions.push(generateSuggestion({
          title: `⚡ Accelerating ${metricName}`,
          insight: `⚡ Your ${metricName} is showing ${trend.momentum.toFixed(0)}% improvement momentum—recent performance is significantly outpacing earlier periods.`,
          actions: [
            "Capture what's driving this acceleration before it changes",
            "Invest more heavily in recent successful initiatives",
            "Ride this momentum wave by launching complementary efforts",
            "Monitor closely to ensure acceleration continues"
          ],
          tags: [...categories, "growth"],
          confidence: 0.82,
          priority: 2,
          dataContext: { metric: trend.metricKey, momentum: trend.momentum }
        }));
      }
    });
  });

  // === ANALYZE STANDALONE NUMERIC DATA ===
  Object.entries(extracted.numeric).forEach(([key, value]) => {
    // Skip excluded fields
    if (shouldExcludeField(key)) {
      return;
    }

    // Skip growth percentage entirely if we have geographic insights
    const lowerKey = key.toLowerCase();
    if ((lowerKey.includes('growth') && (lowerKey.includes('percent') || lowerKey.includes('percentage'))) &&
      (geoData.topCity || geoData.topCountry || geoData.growthMarket)) {
      return;
    }

    // Skip any standalone percentage metrics when we have richer context data
    if ((lowerKey.includes('percent') || lowerKey.includes('percentage') || lowerKey === 'roi') &&
      (hasFinancialData || geoData.topCity || geoData.topCountry)) {
      return;
    }

    const categories = categorizeKey(key);
    const metricName = formatMetricName(key);

    // Significant scale
    if (value >= 1000000) {
      suggestions.push(generateSuggestion({
        title: `💎 Impressive Scale in ${metricName}`,
        insight: `💎 You've achieved ${formatNumber(value)} in ${metricName}—this level of scale unlocks premium opportunities and strategic advantages.`,
        actions: [
          "Leverage this scale as proof of credibility in partnership negotiations",
          "Highlight this achievement in marketing and sales materials",
          "Explore enterprise or institutional opportunities that require scale",
          "Optimize operations to maintain efficiency at this volume"
        ],
        tags: [...categories, "performance"],
        confidence: 0.75,
        priority: 3,
        dataContext: { metric: key, value }
      }));
    }

    // Milestone proximity
    if ((categories.includes("followers") || categories.includes("subscribers") || categories.includes("revenue")) && value >= 100) {
      const nextMilestone = value >= 100000
        ? Math.ceil(value / 100000) * 100000
        : Math.ceil(value / 10000) * 10000;
      const gap = nextMilestone - value;
      const gapPercent = (gap / value) * 100;

      if (gapPercent <= 30 && gapPercent > 0) {
        suggestions.push(generateSuggestion({
          title: `🎯 ${formatNumber(nextMilestone)} ${metricName} Within Reach`,
          insight: `🎯 You're at ${formatNumber(value)} ${metricName}, just ${formatNumber(gap)} away from ${formatNumber(nextMilestone)}—a milestone that signals credibility and momentum.`,
          actions: [
            `Launch a "Road to ${formatNumber(nextMilestone)}" campaign to build excitement`,
            "Offer limited-time incentives to accelerate growth toward the milestone",
            "Create shareable content celebrating progress toward this goal",
            `Plan a major celebration announcement when you hit ${formatNumber(nextMilestone)}`
          ],
          tags: [...categories, "growth"],
          confidence: 0.85,
          priority: gapPercent <= 10 ? 1 : 2,
          dataContext: { metric: key, current: value, milestone: nextMilestone, gap }
        }));
      }
    }

    // Early validation
    if (value > 0 && value < 100 && (categories.includes("revenue") || categories.includes("followers") || categories.includes("customers"))) {
      suggestions.push(generateSuggestion({
        title: `🌱 Early Validation in ${metricName}`,
        insight: `🌱 You've achieved ${formatNumber(value)} ${metricName}—this early traction validates your concept. Now it's time to focus on systematic, scalable growth.`,
        actions: [
          "Interview each of these early supporters to understand their motivation",
          "Double down on the channel that brought these first wins",
          "Set a 30-day goal to triple this number through focused effort",
          "Build rapid feedback loops to improve based on early user insights"
        ],
        tags: [...categories, "growth"],
        confidence: 0.73,
        priority: 3,
        dataContext: { metric: key, value, stage: "early" }
      }));
    }

    // High performance rate/percentage
    if ((key.toLowerCase().includes("rate") || key.toLowerCase().includes("percent")) && value > 15 && value < 100) {
      suggestions.push(generateSuggestion({
        title: `✅ Strong ${metricName}`,
        insight: `✅ Your ${metricName} is at ${formatNumber(value)}%—this exceeds typical industry benchmarks and demonstrates effective execution.`,
        actions: [
          "Document what's driving this above-average performance",
          "Test if you can push this rate even higher with optimization",
          "Use this strong performance as a benchmark for other metrics",
          "Share these tactics across your team or other channels"
        ],
        tags: [...categories, "performance"],
        confidence: 0.78,
        priority: 3,
        dataContext: { metric: key, value }
      }));
    }

    // Low conversion/rate that needs attention
    if ((key.toLowerCase().includes("rate") || key.toLowerCase().includes("conversion")) && value > 0 && value < 5) {
      suggestions.push(generateSuggestion({
        title: `⚡ Opportunity in ${metricName}`,
        insight: `⚡ Your ${metricName} is at ${formatNumber(value)}%—small improvements here could drive significant impact on your overall results.`,
        actions: [
          "Identify the biggest friction points in your funnel",
          "A/B test your call-to-action and value proposition",
          "Simplify the conversion process to reduce drop-off",
          "Benchmark against industry standards to set realistic targets"
        ],
        tags: [...categories, "conversion", "performance"],
        confidence: 0.80,
        priority: 2,
        dataContext: { metric: key, value }
      }));
    }
  });

  // === ANALYZE NESTED OBJECTS ===
  Object.entries(extracted.nested).forEach(([path, obj]) => {
    const keys = Object.keys(obj);

    // Skip individual revenue/expense items - we handle those in financial analysis
    if (path.includes('revenues[') || path.includes('expenses[')) {
      return;
    }

    // Top performer analysis
    if (path.toLowerCase().includes("top") || keys.some(k => k.toLowerCase().includes("name"))) {
      const nameKey = keys.find(k => /name|title|track|item|product/i.test(k) && !shouldExcludeField(k));
      const valueKey = keys.find(k => {
        const val = safeNum(obj[k]);
        return val !== null && !shouldExcludeField(k);
      });

      if (nameKey && valueKey) {
        const itemName = obj[nameKey];
        const itemValue = safeNum(obj[valueKey]);
        const categories = categorizeKey(path);
        const cleanMetricName = formatMetricName(valueKey);

        if (itemValue > 0 && itemName && typeof itemName === 'string' && itemName.trim()) {
          suggestions.push(generateSuggestion({
            title: `⭐ Star Performer: ${itemName}`,
            insight: `⭐ "${itemName}" is your Star performer — this represents your strongest market validation.`,
            actions: [
              `Study "${itemName}" deeply to understand why it resonates with your audience`,
              "Create similar content or products based on these proven insights",
              "Promote this heavily as your flagship offering across all channels",
              "Build a series or campaign that amplifies this success"
            ],
            tags: [...categories, "content", "performance"],
            confidence: 0.82,
            priority: 2,
            dataContext: { item: itemName, metric: valueKey, value: itemValue }
          }));
        }
      }
    }

    // Geographic concentration
    if (keys.some(k => /country|city|region|location/i.test(k))) {
      const locationKey = keys.find(k => /country|city|region|location|name/i.test(k) && !shouldExcludeField(k));
      const valueKey = keys.find(k => {
        const val = safeNum(obj[k]);
        return val !== null && !shouldExcludeField(k);
      });

      if (locationKey && valueKey) {
        const location = formatLocation(obj[locationKey]);
        const value = safeNum(obj[valueKey]);
        const cleanMetricName = formatMetricName(valueKey);

        if (value > 0 && location && typeof location === 'string' && location.trim()) {
          suggestions.push(generateSuggestion({
            title: `🌍 Strong Performance in ${location}`,
            insight: `🌍 ${location} drives ${formatNumber(value)} ${cleanMetricName}—this geographic concentration presents a strategic opportunity for focused expansion and deeper market penetration.`,
            actions: [
              `Launch location-specific campaigns tailored for the ${location} market`,
              `Research local influencers, partners, or distribution channels in ${location}`,
              `Adapt your content and messaging to resonate with ${location}'s cultural preferences`,
              `Identify similar adjacent markets with comparable demographics to ${location}`
            ],
            tags: ["geographic", "growth"],
            confidence: 0.79,
            priority: 3,
            dataContext: { location, metric: valueKey, value }
          }));
        }
      }
    }
  });

  // === COMPARATIVE FUNNEL ANALYSIS ===
  const numericEntries = Object.entries(extracted.numeric).filter(([k]) => !shouldExcludeField(k));
  if (numericEntries.length >= 2) {
    const reachMetrics = numericEntries.filter(([k]) => /reach|impression|view|visitor|traffic/i.test(k));
    const conversionMetrics = numericEntries.filter(([k]) => /follower|subscriber|conversion|sale|purchase|signup|lead/i.test(k));

    if (reachMetrics.length > 0 && conversionMetrics.length > 0) {
      const reach = reachMetrics[0];
      const conversion = conversionMetrics[0];
      const conversionRate = (conversion[1] / reach[1]) * 100;

      const reachName = formatMetricName(reach[0]);
      const conversionName = formatMetricName(conversion[0]);

      if (conversionRate < 10) {
        suggestions.push(generateSuggestion({
          title: "🔍 Conversion Funnel Gap",
          insight: `🔍 You're achieving ${formatNumber(reach[1])} ${reachName} but only converting ${formatNumber(conversion[1])} to ${conversionName} (${conversionRate.toFixed(2)}%)—there's significant opportunity in optimizing your funnel.`,
          actions: [
            "Map your complete funnel to identify where users drop off",
            "Add compelling calls-to-action at every touchpoint",
            "Reduce friction by simplifying your conversion process",
            "Run retargeting campaigns for visitors who didn't convert"
          ],
          tags: ["engagement", "conversion", "performance"],
          confidence: 0.86,
          priority: 2,
          dataContext: { reach: reach[1], conversions: conversion[1], rate: conversionRate }
        }));
      } else if (conversionRate >= 10) {
        suggestions.push(generateSuggestion({
          title: "✨ Strong Conversion Efficiency",
          insight: `✨ Your ${conversionRate.toFixed(1)}% conversion rate (${formatNumber(conversion[1])} ${conversionName} from ${formatNumber(reach[1])} ${reachName}) is performing well—focus on scaling reach while maintaining quality.`,
          actions: [
            "Increase top-of-funnel traffic to leverage your strong conversion",
            "Document what's driving your above-average conversion rate",
            "Test if you can push conversion even higher through optimization",
            "Expand to new channels while maintaining conversion quality"
          ],
          tags: ["conversion", "growth"],
          confidence: 0.81,
          priority: 2,
          dataContext: { reach: reach[1], conversions: conversion[1], rate: conversionRate }
        }));
      }
    }
  }

  // === ENSURE MINIMUM SUGGESTIONS ===
  // Only add fallback suggestions if we don't have rich data (financial, geographic, or content)
  const hasRichData = hasFinancialData ||
    (geoData.topCity || geoData.topCountry || geoData.growthMarket) ||
    (contentData.trackName && contentData.streams !== null);

  if (suggestions.length === 0 && !hasRichData) {
    const allMetrics = [
      ...Object.entries(extracted.numeric).filter(([k]) => !shouldExcludeField(k)),
      ...Object.entries(extracted.text).filter(([k]) => !shouldExcludeField(k)),
      ...Object.entries(extracted.dates).filter(([k]) => !shouldExcludeField(k))
    ];

    if (allMetrics.length > 0) {
      // Create insights from available data
      const sampleMetrics = allMetrics.slice(0, 3);

      suggestions.push(generateSuggestion({
        title: "🎯 Turn Data into Action",
        insight: `🎯 I found ${allMetrics.length} meaningful data point${allMetrics.length > 1 ? 's' : ''} in your dataset. Let's transform this information into strategic growth opportunities.`,
        actions: [
          "Set clear 30-day goals for your most important metrics",
          "Establish daily or weekly tracking to spot trends early",
          "Add complementary metrics to get a complete performance picture",
          "Run one focused experiment this week to move the needle"
        ],
        tags: ["growth", "performance"],
        confidence: 0.68,
        priority: 2,
        dataContext: { availableMetrics: allMetrics.length }
      }));

      // If we have numeric data, provide specific guidance
      if (Object.keys(extracted.numeric).length > 0) {
        const numericMetrics = Object.entries(extracted.numeric)
          .filter(([k]) => !shouldExcludeField(k))
          .slice(0, 2);
        const metricNames = numericMetrics
          .map(([k]) => formatMetricName(k))
          .join(' and ');

        if (metricNames) {
          suggestions.push(generateSuggestion({
            title: "📊 Build Momentum with Tracking",
            insight: `📊 Your ${metricNames} data shows you're measuring what matters. Consistent tracking over time will reveal actionable patterns and opportunities.`,
            actions: [
              "Track these metrics consistently for at least 2 weeks to establish trends",
              "Set benchmarks based on industry standards or past performance",
              "Create a simple dashboard to monitor progress at a glance",
              "Return with time-series data for deeper, trend-based insights"
            ],
            tags: ["performance", "growth"],
            confidence: 0.70,
            priority: 3,
            dataContext: { metrics: numericMetrics.map(([k, v]) => ({ key: k, value: v })) }
          }));
        }
      }

      // Suggest data enrichment
      suggestions.push(generateSuggestion({
        title: "🚀 Unlock Deeper Insights",
        insight: "🚀 Your current data provides a snapshot, but tracking changes over time will reveal powerful growth opportunities and early warnings.",
        actions: [
          "Set up comprehensive analytics across all your key platforms",
          "Implement weekly data collection to track trends",
          "Add metrics for user behavior, conversion points, and engagement",
          "Create a data routine: collect on Mondays, analyze on Fridays"
        ],
        tags: ["performance", "growth"],
        confidence: 0.65,
        priority: 4,
        dataContext: { dataQuality: "limited" }
      }));
    } else {
      // Absolute fallback when no usable data
      suggestions.push(generateSuggestion({
        title: "📍 Start with Clear Metrics",
        insight: "📍 Data-driven growth starts with measuring what matters. Let's establish the foundation for strategic decision-making.",
        actions: [
          "Define your 3 North Star metrics (e.g., revenue, users, engagement)",
          "Set up analytics tools to automatically track these metrics",
          "Measure for one week to establish your baseline",
          "Return with this data for actionable, personalized strategies"
        ],
        tags: ["performance"],
        confidence: 0.60,
        priority: 4,
        dataContext: { status: "no_metrics" }
      }));

      suggestions.push(generateSuggestion({
        title: "🎓 Learn from Your Data",
        insight: "🎓 The most successful strategies are built on understanding patterns in your data. Let's make data collection a competitive advantage.",
        actions: [
          "Research which metrics top performers in your space track",
          "Choose 5-7 key metrics that align with your goals",
          "Set up weekly automated reports to your email",
          "Block 30 minutes each week to review trends and adjust strategy"
        ],
        tags: ["growth", "performance"],
        confidence: 0.58,
        priority: 5,
        dataContext: { stage: "setup" }
      }));
    }
  }

  // === ENSURE MINIMUM OF 2 SUGGESTIONS ===
  // Only add generic suggestions if we don't have rich financial or geographic data
  while (suggestions.length < 2 && !hasRichData) {
    if (Object.keys(extracted.numeric).length > 0 || Object.keys(extracted.text).length > 0) {
      suggestions.push(generateSuggestion({
        title: "💡 Optimize Your Approach",
        insight: "💡With your current data foundation, focus on consistent measurement and rapid experimentation to accelerate growth.",
        actions: [
          "Test one new tactic each week and measure results",
          "Compare your performance to industry benchmarks",
          "Ask customers what would make them engage more",
          "Prioritize quick wins that can compound over time"
        ],
        tags: ["growth", "performance"],
        confidence: 0.67,
        priority: 3,
        dataContext: { type: "optimization" }
      }));
    } else {
      suggestions.push(generateSuggestion({
        title: "🔧 Build Your Data Foundation",
        insight: "🔧 Great strategies require great data. Invest time in proper setup now to unlock exponential insights later.",
        actions: [
          "Choose analytics platforms that fit your needs and budget",
          "Set up goal tracking for conversions and key actions",
          "Create a shared dashboard for your team to monitor progress",
          "Schedule monthly reviews to spot opportunities and threats"
        ],
        tags: ["performance"],
        confidence: 0.62,
        priority: 4,
        dataContext: { stage: "foundation" }
      }));
    }
  }

  // === SORT BY PRIORITY & CONFIDENCE ===
  suggestions.sort((a, b) => {
    // Prioritize financial insights when available
    const aIsFinancial = a.tags.includes("revenue");
    const bIsFinancial = b.tags.includes("revenue");
    if (aIsFinancial && !bIsFinancial) return -1;
    if (!aIsFinancial && bIsFinancial) return 1;

    // Then prioritize content/track insights
    const aIsContent = a.tags.includes("streams") || a.tags.includes("content");
    const bIsContent = b.tags.includes("streams") || b.tags.includes("content");
    if (aIsContent && !bIsContent) return -1;
    if (!aIsContent && bIsContent) return 1;

    // Then prioritize geographic insights
    const aIsGeo = a.tags.includes("geographic");
    const bIsGeo = b.tags.includes("geographic");
    if (aIsGeo && !bIsGeo) return -1;
    if (!aIsGeo && bIsGeo) return 1;

    // Then sort by priority
    if (a.priority !== b.priority) return a.priority - b.priority;

    // Finally by confidence
    return b.confidence - a.confidence;
  });

  // Return top 3 most relevant suggestions
  const topSuggestions = suggestions.slice(0, 3);

  // Assess data quality
  let dataQuality = "limited";
  const totalMetrics = Object.keys(extracted.numeric).length +
    Object.keys(extracted.text).length +
    Object.keys(extracted.arrays).length;

  const hasContentData = contentData.trackName && contentData.streams !== null;

  if (hasFinancialData || (geoData.topCity || geoData.topCountry) || hasContentData) {
    dataQuality = "rich";
  } else if (totalMetrics > 10) {
    dataQuality = "rich";
  } else if (totalMetrics > 3) {
    dataQuality = "moderate";
  }

  return {
    suggestions: topSuggestions,
    totalAnalyzed: suggestions.length,
    highestConfidence: topSuggestions[0]?.confidence || 0.6,
    dataSnapshot: {
      numericFields: Object.keys(extracted.numeric).length,
      textFields: Object.keys(extracted.text).length,
      arrayFields: Object.keys(extracted.arrays).length,
      nestedObjects: Object.keys(extracted.nested).length,
      dateFields: Object.keys(extracted.dates).length,
      booleanFields: Object.keys(extracted.booleans).length
    },
    recommendations: {
      dataQuality,
      nextSteps: topSuggestions.length > 0
        ? "Focus on implementing the highest priority action items"
        : "Improve data collection for better insights"
    }
  };
}